import React from 'react';
import { colors, fontFamily, ghostButton, iconButton, primaryButton } from './editor.styles';
import { CheckIcon, CloseIcon, MinusIcon, PlusIcon, PointerIcon } from './icons';

export interface EditorFeedback {
  message: string;
  canUndo: boolean;
}

interface SelectionToolbarProps {
  selectedCount: number;
  metricLabel: string;
  bulkValue: string;
  feedback: EditorFeedback | null;
  onBulkChange: (value: string) => void;
  onApply: (value: number | undefined) => void;
  onDeselect: () => void;
  onUndo: () => void;
}

const HINT = 'Cliquez sur des créneaux pour les modifier ensemble. Un clic sur un jour, un horaire ou un service sélectionne toute la colonne ou la ligne.';

export const parseLimit = (value: string) => {
  const parsed = parseInt(value, 10);

  return isNaN(parsed) || parsed < 0 ? 0 : parsed;
};

export const SelectionToolbar = ({ selectedCount, metricLabel, bulkValue, feedback, onBulkChange, onApply, onDeselect, onUndo }: SelectionToolbarProps) => {
  const stepperButton = { ...iconButton, borderRadius: 0, color: colors.text };

  return (
    // Hauteur fixe : la grille juste en dessous ne doit pas bouger quand le contenu de la barre change
    <div style={{ height: 60, flexShrink: 0, boxSizing: 'border-box', padding: '0 10px 0 16px', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden', whiteSpace: 'nowrap' }}>
      {selectedCount > 0 ? (
        <>
          <span style={{ fontSize: 14, fontWeight: 700, color: colors.selection }}>
            {selectedCount} créneau{selectedCount > 1 ? 'x' : ''} sélectionné{selectedCount > 1 ? 's' : ''}
          </span>
          <span style={{ fontSize: 13, color: colors.muted }}>· limite {metricLabel.toLowerCase()}</span>

          <span style={{ flexGrow: 1 }} />

          <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${colors.borderStrong}`, borderRadius: 8, overflow: 'hidden' }}>
            <button type="button" aria-label="Diminuer" onClick={() => onBulkChange(String(Math.max(0, parseLimit(bulkValue) - 1)))} style={stepperButton}>
              <MinusIcon />
            </button>
            <input
              type="number"
              min={0}
              value={bulkValue}
              aria-label="Valeur à appliquer"
              onChange={(event) => onBulkChange(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && onApply(parseLimit(bulkValue))}
              style={{ width: 60, height: 40, boxSizing: 'border-box', border: 'none', borderLeft: `1px solid ${colors.borderStrong}`, borderRight: `1px solid ${colors.borderStrong}`, background: colors.background, color: colors.text, fontFamily, fontSize: 16, fontWeight: 700, textAlign: 'center' }} />
            <button type="button" aria-label="Augmenter" onClick={() => onBulkChange(String(parseLimit(bulkValue) + 1))} style={stepperButton}>
              <PlusIcon />
            </button>
          </div>

          <button type="button" onClick={() => onApply(parseLimit(bulkValue))} style={primaryButton}>Appliquer</button>
          <button type="button" onClick={() => onApply(undefined)} style={ghostButton}>Sans limite</button>
          <button type="button" onClick={() => onApply(0)} style={{ ...ghostButton, border: '1px solid rgba(255,120,120,0.35)', color: colors.dangerText }}>Bloquer (0)</button>
          <button type="button" aria-label="Désélectionner" onClick={onDeselect} style={iconButton}>
            <CloseIcon />
          </button>
        </>
      ) : (
        <>
          <span style={{ color: colors.accent, display: 'flex', flexShrink: 0 }}><PointerIcon /></span>
          <span
            title={HINT}
            style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 14, color: colors.soft }}>
            {HINT}
          </span>

          {feedback && (
            <span style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '55%', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: colors.success }}>
              <span style={{ display: 'flex', flexShrink: 0 }}><CheckIcon /></span>
              <span title={feedback.message} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{feedback.message}</span>
            </span>
          )}

          {feedback?.canUndo && (
            <button type="button" onClick={onUndo} style={{ ...ghostButton, flexShrink: 0, height: 36, padding: '0 12px', fontSize: 13 }}>Annuler</button>
          )}
        </>
      )}
    </div>
  );
};
